import {
  reconcileBiometricUser,
  clearPersistedNativeSession,
  persistNativePrimarySession,
} from "@features/BiometricAuthentication/biometricUnlock";
import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { Platform } from "react-native";

import { buildStaffAppUrl } from "@/utils/getStaffAppUrlString";
import { useFlags } from "@/utils/launchDarkly";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  clearNativeSessionSignedOut,
  resumeNativeSessionPersistence,
} from "@whoosh/relay/src";

import { useMutationWithError } from "../../../../../../utils/hooks/useMutationWithError";
import { currentProfileAtom } from "../../../../../Club/atoms";
import { sessionAtom } from "../../../../../Session";
import { isUserAuthenticated_atom } from "../../../../atoms";
import {
  ProceedToAccountCreationStepAtom,
  proceedToJoinNewClubStepAtom,
} from "../atoms/actions";
import { verificationCodeStepDataAtom } from "../atoms/selectors";
import { VerificationModalCompleteActionAtom } from "../atoms/stores";
import {
  shouldRetryAccountCreationAfterVerification,
  useTryToCreateAccount,
} from "../components/VerifiedAuthenticationModalBody/components/AccountCreationStep/hooks/useTryToCreateAccount";
import { CreateCustomerUserInput } from "./__generated__/useCreateNewCustomerUserMutation.graphql";
import { useVerifyEmailCodeMutation } from "./__generated__/useVerifyEmailCodeMutation.graphql";

export const useVerifyEmailCode = () => {
  const { biometricAuth } = useFlags();
  const [verifyEmailCode, isVerifying] =
    useMutationWithError<useVerifyEmailCodeMutation>(
      useVerifyEmailCodeMutationString
    );

  const proceedToAccountCreation = useSetAtom(ProceedToAccountCreationStepAtom);
  const setIsUserAuthenticated = useSetAtom(isUserAuthenticated_atom);
  const setSession = useSetAtom(sessionAtom);
  const setCurrentProfile = useSetAtom(currentProfileAtom);
  const completeAction = useAtomValue(VerificationModalCompleteActionAtom);
  const proceedToJoinClub = useSetAtom(proceedToJoinNewClubStepAtom);
  const verificationStepState = useAtomValue(verificationCodeStepDataAtom);
  const [tryToCreateAccount, isCreating] = useTryToCreateAccount();

  const { showToast } = useToast();
  const handleEmailCodeVerification = ({
    email,
    code,
    onError,
  }: {
    email: string;
    code: string;
    onError: () => void;
  }) => {
    const startVerification = () => {
      if (Platform.OS !== "web") {
        resumeNativeSessionPersistence();
      }

      verifyEmailCode({
        variables: { input: { email, code } },
        onCompleted: async (res) => {
          if (
            res.responsePayload?.success &&
            res.responsePayload.session?.user &&
            Platform.OS !== "web"
          ) {
            const authenticatedUserId = res.responsePayload?.session?.user?.id;
            if (authenticatedUserId && biometricAuth === true) {
              await reconcileBiometricUser(authenticatedUserId);
            }
            await persistNativePrimarySession();
            await clearNativeSessionSignedOut();
          }

          /**
           * Are we creating a new account?
           */
          if (
            shouldRetryAccountCreationAfterVerification({
              hasAssignmentToken: !!res.responsePayload?.assignmentToken,
              hasAuthenticatedUser: !!res.responsePayload?.session?.user,
              hasFormDataToSubmit: !!verificationStepState?.formDataToSubmit,
              verificationType: verificationStepState?.verificationType,
            }) &&
            res?.responsePayload?.assignmentToken &&
            verificationStepState?.formDataToSubmit
          ) {
            //If so, get the assignment token, patch it into our form data, and fire create again
            const currentFormData: CreateCustomerUserInput = {
              ...verificationStepState.formDataToSubmit,
              emailAssignmentToken: res.responsePayload.assignmentToken,
            };
            tryToCreateAccount(currentFormData);
          }

          const user = res.responsePayload?.session?.user;

          const hasAssignmentTokenButNoUser =
            !user &&
            res.responsePayload?.success &&
            res.responsePayload.assignmentToken;

          const hasExistingMemberUser =
            res.responsePayload?.session?.user?.__typename === "MemberUser";

          if (hasAssignmentTokenButNoUser) {
            //Assume we verified so lets go on to the next step.
            proceedToAccountCreation({
              verifiedEmail: email,
              verifiedPhone: null,
              emailAssignmentToken: res.responsePayload.assignmentToken,
              phoneAssignmentToken: null,
            });
          } else if (hasExistingMemberUser) {
            const currentProfile =
              res.responsePayload?.session?.currentClientProfile;

            if (user) {
              setIsUserAuthenticated(true);
              setSession({
                user: {
                  id: user.id,
                  email: user.email!,
                  username: user.username!,
                  clubs:
                    user.memberProfiles?.edges?.map((edge) => ({
                      id: edge?.node?.member?.club?.id || "",
                      slug: edge?.node?.member?.club?.slug || "",
                      name: edge?.node?.member?.club?.name || "",
                      logoImage:
                        edge?.node?.member?.club?.logoImage?.publicId || "",
                    })) || [],
                },
              });
              if (
                currentProfile?.member?.__typename === "Member" ||
                currentProfile?.member?.__typename === "Customer"
              ) {
                const ghinNumber =
                  currentProfile?.member?.departmentMemberProfiles?.find(
                    (dmp) => !!dmp?.ghinNumber
                  )?.ghinNumber;
                setCurrentProfile({
                  memberId: currentProfile?.member?.id || "",
                  firstName: currentProfile?.member?.firstName || "",
                  lastName: currentProfile?.member?.lastName || "",
                  membershipTypeId:
                    currentProfile?.member?.membershipType?.id ?? "",
                  memberGroups: [],
                  hasSeenNotificationNudgeModal:
                    user?.metadata?.hasSeenNotificationNudgeModal || false,
                  email: currentProfile?.member?.email || "",
                  phoneNumber:
                    currentProfile?.member?.phoneNumber?.nationalNumber || "",

                  ghinNumber,
                });
              } else {
                setCurrentProfile(null);
              }
            }
            const clientType =
              res.responsePayload?.session?.currentClientProfile?.member
                ?.__typename;
            const needsMemberProfile = clientType === "AnonymousCustomer";
            if (needsMemberProfile) {
              proceedToJoinClub();
            } else {
              const firstProfile =
                res.responsePayload.session?.user?.memberProfiles?.edges?.[0]
                  ?.node;
              const defaultClubSlug = firstProfile?.member?.club?.slug;

              completeAction?.(defaultClubSlug);
            }
          } else {
            /**
             * This is a staff user. On web we ship them to the staff app.
             *
             * On native we just show them a toast because we can't ship them anywhere.
             */
            setIsUserAuthenticated(false);
            if (Platform.OS === "web") {
              window.location.href = buildStaffAppUrl(window.location);
            } else {
              clearPersistedNativeSession().then(() => {
                setIsUserAuthenticated(false);
                showToast({
                  description: t`Whoops! For now, only members can log into the mobile application.`,
                  semanticStyle: "error",
                  toastId: "staff-login-error",
                });
              });
            }
          }
        },
        onCompletedErrorCustom(errors) {
          onError();
          return errors;
        },
      });
    };

    startVerification();
  };
  return [handleEmailCodeVerification, isVerifying || isCreating] as const;
};
export const useVerifyEmailCodeMutationString = graphql`
  mutation useVerifyEmailCodeMutation($input: VerifyEmailCodeInput!) {
    responsePayload: verifyEmailCode(input: $input) {
      success
      assignmentToken
      errors {
        field
        code
        message
      }
      session {
        ...useSyncAnalyticsTrackingPermission_session
        currentClientProfile {
          ... on MemberProfile {
            id
            active
          }
          member {
            ... on Client {
              ... on AnonymousCustomer {
                __typename
              }
              ... on Customer {
                departmentMemberProfiles(type: GOLF) {
                  ... on GolfMemberProfile {
                    department {
                      slug
                    }
                    ghinNumber
                  }
                }
                membershipType {
                  id
                }
                __typename
                id
                firstName
                lastName
                fullName
                email
                phoneNumber {
                  nationalNumber
                }
                profileImage {
                  # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                  publicId: renderingKey
                }
              }
              ... on Member {
                departmentMemberProfiles(type: GOLF) {
                  ... on GolfMemberProfile {
                    department {
                      slug
                    }
                    ghinNumber
                  }
                }
                membershipType {
                  id
                }
                __typename
                id
                firstName
                lastName
                fullName
                email
                phoneNumber {
                  nationalNumber
                }
                profileImage {
                  # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                  publicId: renderingKey
                }
                memberGroups {
                  id
                }
              }
            }
          }
        }
        masquerader {
          email
          username
        }
        user {
          __typename
          id
          email
          username
          ... on MemberUser {
            memberProfiles(active: true) {
              edges {
                node {
                  member {
                    club {
                      id
                      name
                      slug
                      logoImage {
                        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                        publicId: renderingKey
                      }
                    }
                  }
                }
              }
            }
            metadata {
              hasSeenNotificationNudgeModal
            }
          }
        }
      }
    }
  }
`;
